// src/modules/assetManagement/library/LibraryPage.tsx
// Library shell: Events, Products and Assets tabs, each rendered below as a child route.
import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { CalendarDays, Image as ImageIcon, Library, Package } from "lucide-react";
import { ROUTES } from "../utils";

const tabClass = "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition";

// One page shows Events, Products or Assets; the chosen one renders below as a child route
const TABS = [
  { to: ROUTES.libraryEvents, label: "Events", Icon: CalendarDays, active: "bg-blue-600 text-white", idle: "text-blue-700 hover:bg-blue-50" },
  { to: ROUTES.libraryProducts, label: "Products", Icon: Package, active: "bg-amber-700 text-white", idle: "text-amber-800 hover:bg-amber-50" },
  { to: ROUTES.libraryAssets, label: "Assets", Icon: ImageIcon, active: "bg-emerald-700 text-white", idle: "text-emerald-700 hover:bg-emerald-50" },
];

// The open Event / Product / Asset page renders its breadcrumb into this element
// (see CampaignsAndEvents.tsx), so it sits between the app header and "Library".
// Keep this id in sync with LIBRARY_BREADCRUMB_SLOT_ID in CampaignsAndEvents.tsx.
const BREADCRUMB_SLOT_ID = "library-breadcrumb-slot";

// Finds the app's top bar ("Acme Retail" header), keeps it pinned while you
// scroll the Library, and reports what the breadcrumb needs to attach to it:
// the header's height (where to pin) and the space between the header and the
// Library content (pulled up so there's no gap).
const useStickyAppHeader = (ref: React.RefObject<HTMLElement | null>) => {
  const [layout, setLayout] = useState({
    headerHeight: 0,
    gapAbove: 0,
    insetLeft: 0,
    insetRight: 0,
  });

  useEffect(() => {
    const root = ref.current?.parentElement; // LibraryPage's outer <div>
    if (!root) return;

    const isPinned = (el: Element) => {
      const pos = getComputedStyle(el).position;
      return pos === "sticky" || pos === "fixed";
    };

    // The top bar is an earlier sibling of one of our ancestors that sits at the
    // very top of the page, is short and spans most of the width.
    let found: HTMLElement | null = null;
    for (
      let node: HTMLElement | null = root;
      node && node !== document.body && !found;
      node = node.parentElement
    ) {
      for (let sib = node.previousElementSibling; sib; sib = sib.previousElementSibling) {
        const r = sib.getBoundingClientRect();
        const atTop = r.top + window.scrollY <= 4 || isPinned(sib);
        if (atTop && r.height > 0 && r.height < window.innerHeight * 0.4 && r.width >= window.innerWidth * 0.5) {
          found = sib as HTMLElement;
          break;
        }
      }
    }
    if (!found) return;
    const header = found;

    // Keep the header on screen while the Library is open (undone when leaving)
    const original = {
      position: header.style.position,
      top: header.style.top,
      zIndex: header.style.zIndex,
    };
    if (!isPinned(header)) {
      header.style.position = "sticky";
      header.style.top = "0px";
      header.style.zIndex = "40";
    }

    const measure = () => {
      const h = header.getBoundingClientRect();
      const c = root.getBoundingClientRect();
      const next = {
        headerHeight: Math.round(h.height),
        gapAbove: Math.max(0, Math.round(c.top + window.scrollY - h.height)),
        // Space between the header's edges and the Library content's edges
        insetLeft: Math.max(0, Math.round(c.left - h.left)),
        insetRight: Math.max(0, Math.round(h.right - c.right)),
      };
      setLayout((prev) =>
        prev.headerHeight === next.headerHeight &&
          prev.gapAbove === next.gapAbove &&
          prev.insetLeft === next.insetLeft &&
          prev.insetRight === next.insetRight
          ? prev
          : next,
      );
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(header);
    ro.observe(root);
    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      Object.assign(header.style, original);
    };
  }, [ref]);

  return layout;
};

const LibraryPage: React.FC = () => {
  const slotRef = useRef<HTMLDivElement>(null);
  const { headerHeight, gapAbove, insetLeft, insetRight } = useStickyAppHeader(slotRef);

  return (
    // flow-root: stops the slot's negative margin from moving this whole block
    <div className="max-w-[1500px] mx-auto flow-root">
      {/* Breadcrumb bar: full width like the header and attached right under it
          (no top or side gaps), pinned with it while scrolling. The breadcrumb
          text lines up with the page content. Hidden when empty (list pages). */}
      <div
        ref={slotRef}
        id={BREADCRUMB_SLOT_ID}
        className="sticky z-30 mb-6 empty:hidden bg-white border-b border-gray-200 shadow-sm py-2.5"
        style={{
          top: headerHeight,
          marginTop: -gapAbove,
          marginLeft: -insetLeft,
          marginRight: -insetRight,
          paddingLeft: Math.max(16, insetLeft),
          paddingRight: Math.max(16, insetRight),
        }}
      />

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
  {/* Create / Edit Event heading is mounted here */}
  <div
    id="library-heading-slot"
    className="flex items-center flex-1 min-w-0"
  />

  {/* Existing Events / Products / Assets tabs */}
  <nav
    className="flex gap-1 p-1 bg-white border border-slate-200 rounded-xl shadow-sm shrink-0"
    aria-label="Library views"
  >
    {TABS.map(({ to, label, Icon, active, idle }) => (
      <NavLink
        key={to}
        to={to}
        className={({ isActive }) =>
          `${tabClass} ${isActive ? active : idle}`
        }
      >
        <Icon size={16} /> {label}
      </NavLink>
    ))}
  </nav>
</div>
      <Outlet />
    </div>
  );
};

export default LibraryPage;
